import { authSplitApi } from 'redux/helpers/slice.helpers';
import {
  TAddCustomTacticRequest,
  TAddCustomTacticResponse,
  TAddTacticToActionPlanRequest,
  TAddTacticToActionPlanResponse,
  TTask,
  TUpdateCustomTacticRequest,
  TUpdateCustomTacticResponse,
} from 'data/types/pillarsActionPlan.types';
import {
  TExportCsvDB,
  TExportCsvUI,
  TPageableDataWithContentUI,
  TTableFilters,
} from '../../data/types/generic.types';
import { exportCsvConverter } from '../../data/convertors/general.converters';
import { downloadFile } from '../services/fileServices';
import { elcsApi } from './elcs.slice';

const baseUrl = '/pillars/action-plans';

export interface TGetActionPlansRequest {
  elcId?: string;
  tacticId?: string;
  pillars?: string[];
  tacticTitles?: string[];
  sortDirection?: 'ASC' | 'DESC';
  sortField?: 'CREATION_DATE' | 'PILLAR' | 'TACTIC_TITLE' | 'dateCompleted';
  statuses?: ('TODO' | 'IN_PROGRESS' | 'STALLED' | 'COMPLETED')[];
  size?: number;
  page?: number;
}

export interface TActionPlanResponse {
  pillarId: string;
  pillarName: string;
  pillarOrder: number;
  actionPlanId: number;
  tacticId: number;
  tacticTitle: string;
  todoCount: number;
  inProgressCount: number;
  stalledCount: number;
  completedCount: number;
  repeatOrder: number;
  isCustomTactic: boolean;
  isPreviousPlan: boolean;
  status?: 'TODO' | 'IN_PROGRESS' | 'STALLED' | 'COMPLETED';
  dateCompleted?: string | null;
  isGlobal: boolean;
  elcId: string | null;
  elcName: string | null;
  goalId?: string | null;
  goalDescription?: string | null;
  levelId?: string | null;
  levelName?: string | null;
}

export interface TGetActionPlansResponse {
  page: {
    size: number;
    content: TActionPlanResponse[];
    number: number;
    totalElements: number;
    totalPages: number;
  };
  filters: {
    tacticTitles: string[];
  };
  elcLead: boolean;
}

export const pillarsActionPlanApi = authSplitApi('pillarsActionPlan', [
  'custom-tactics',
  'action-plans',
]).injectEndpoints({
  endpoints: build => ({
    getActionPlans: build.query<
      TGetActionPlansResponse,
      TGetActionPlansRequest
    >({
      query: ({
        elcId,
        tacticId,
        pillars,
        tacticTitles,
        statuses,
        sortDirection,
        sortField,
        size,
        page,
      }) => {
        const params = new URLSearchParams();

        if (elcId) params.append('elcId', elcId);
        if (tacticId) params.append('tacticId', tacticId);
        if (pillars && pillars.length > 0) {
          pillars.forEach(pillar => params.append('pillars', pillar));
        }
        if (tacticTitles && tacticTitles.length > 0) {
          tacticTitles.forEach(tacticTitle =>
            params.append('tacticTitles', tacticTitle),
          );
        }
        if (statuses && statuses.length > 0) {
          statuses.forEach(status => params.append('statuses', status));
        }
        if (sortDirection) params.append('sortDirection', sortDirection);
        if (sortField) params.append('sortField', sortField);
        if (size) params.append('size', size.toString());
        if (page) params.append('page', page.toString());

        return {
          url: `${baseUrl}?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['action-plans'],
    }),

    addCustomTactic: build.mutation<
      TAddCustomTacticResponse,
      TAddCustomTacticRequest
    >({
      query(body) {
        return {
          url: `${baseUrl}/custom-tactics`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['custom-tactics', 'action-plans'],
    }),

    addTacticToActionPlan: build.mutation<
      TAddTacticToActionPlanResponse,
      TAddTacticToActionPlanRequest
    >({
      query(body) {
        return {
          url: baseUrl,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [
        'action-plans',
        'custom-tactics',
        'pillars-of-strength',
      ],
    }),

    updateActionPlan: build.mutation<
      {
        actionPlanId: number;
        status: 'TODO' | 'IN_PROGRESS' | 'STALLED' | 'COMPLETED';
        dateCompleted: string | null;
      },
      {
        actionPlanId: number;
        field: 'STATUS' | 'DATE_COMPLETED';
        value: string;
      }
    >({
      query({ actionPlanId, field, value }) {
        return {
          url: `${baseUrl}/${actionPlanId}`,
          method: 'PATCH',
          body: { field, value },
        };
      },
      invalidatesTags: ['action-plans'],
    }),

    createTask: build.mutation<
      TTask,
      {
        actionPlanId: number;
        taskTitle: string;
      }
    >({
      query({ actionPlanId, taskTitle }) {
        return {
          url: `${baseUrl}/${actionPlanId}/task`,
          method: 'POST',
          body: { taskTitle },
        };
      },
      invalidatesTags: ['tasks', 'action-plans'],
    }),
    createTaskFromMeeting: build.mutation<
      TTask[],
      {
        actionPlanId: number;
        meetingActionItemIds: string[];
      }
    >({
      query({ actionPlanId, meetingActionItemIds }) {
        return {
          url: `${baseUrl}/${actionPlanId}/task/convert-from-meeting`,
          method: 'POST',
          body: { actionPlanId, meetingActionItemIds },
        };
      },
      invalidatesTags: ['tasks', 'action-plans'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              elcsApi.util.invalidateTags([
                'elcs-meetings-action-items',
                'elcs-meetings',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    updateTask: build.mutation<
      TTask,
      {
        taskId?: number;
        field?: string;
        value?: string;
      }
    >({
      query({ taskId, field, value }) {
        return {
          url: `${baseUrl}/task/${taskId}`,
          method: 'PATCH',
          body: { field, value },
        };
      },
      invalidatesTags: ['tasks', 'action-plans'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              elcsApi.util.invalidateTags([
                'elcs-meetings-action-items',
                'elcs-meetings',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    deleteTask: build.mutation<void, { taskId: number }>({
      query({ taskId }) {
        return {
          url: `${baseUrl}/task/${taskId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['tasks', 'action-plans'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              elcsApi.util.invalidateTags([
                'elcs-meetings-action-items',
                'elcs-meetings',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    getTasks: build.query<
      TPageableDataWithContentUI<TTask[]>,
      TTableFilters<'taskTitle' | 'assignee' | 'status'> & {
        actionPlanId?: number;
      }
    >({
      query({
        page = 1,
        pageSize,
        sortColumn: { sortDirection, sortField },
        actionPlanId,
        filters,
      }) {
        return {
          url: `${baseUrl}/tasks`,
          method: 'GET',
          params: {
            page,
            size: pageSize,
            ...(sortField && sortDirection && { sortField, sortDirection }),
            actionPlanId,
            ...(filters?.taskTitle && {
              taskTitle: filters.taskTitle,
            }),
            ...(filters?.assignee &&
              filters.assignee.length > 0 && {
                assignee: filters.assignee.join(','),
              }),
            ...(filters?.status &&
              filters.status.length > 0 && {
                status: filters.status.join(','),
              }),
          },
        };
      },
      providesTags: ['tasks'],
    }),
    updateCustomTactic: build.mutation<
      TUpdateCustomTacticResponse,
      TUpdateCustomTacticRequest
    >({
      query(body) {
        const requestBody: Record<string, unknown> = {
          actionPlanId: body.actionPlanId,
          title: body.title,
          pillarsId: body.pillarsId,
        };
        if (body.subPillarId) requestBody.subPillarId = body.subPillarId;
        if (body.levelId) requestBody.levelId = body.levelId;

        return {
          url: `${baseUrl}/custom-tactics/${body.actionPlanId}`,
          method: 'PATCH',
          body: requestBody,
        };
      },
      invalidatesTags: ['action-plans', 'custom-tactics'],
    }),

    deleteCustomTactic: build.mutation<void, { actionPlanId: number }>({
      query({ actionPlanId }) {
        return {
          url: `${baseUrl}/${actionPlanId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['action-plans', 'custom-tactics'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              elcsApi.util.invalidateTags([
                'elcs-meetings-action-items',
                'elcs-meetings',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),

    exportData: build.query<
      TExportCsvUI,
      Omit<TGetActionPlansRequest, 'size' | 'page'> & { elcId: string }
    >({
      query: ({
        elcId,
        tacticId,
        pillars,
        tacticTitles,
        statuses,
        sortDirection,
        sortField,
      }) => {
        const params = new URLSearchParams();

        params.append('elcId', elcId);
        if (tacticId) params.append('tacticId', tacticId);
        if (pillars && pillars.length > 0) {
          pillars.forEach(pillar => params.append('pillars', pillar));
        }
        if (tacticTitles && tacticTitles.length > 0) {
          tacticTitles.forEach(tacticTitle =>
            params.append('tacticTitles', tacticTitle),
          );
        }
        if (statuses && statuses.length > 0) {
          statuses.forEach(status => params.append('statuses', status));
        }
        if (sortDirection) params.append('sortDirection', sortDirection);
        if (sortField) params.append('sortField', sortField);

        return {
          url: `${baseUrl}/export?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TExportCsvDB) => {
        const convertedData = exportCsvConverter('zip').fromDb(data);
        downloadFile(convertedData.content, convertedData.fileName);
        return convertedData;
      },
    }),
  }),
});

export const {
  useGetActionPlansQuery,
  useAddCustomTacticMutation,
  useAddTacticToActionPlanMutation,
  useCreateTaskMutation,
  useCreateTaskFromMeetingMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useGetTasksQuery,
  useLazyGetTasksQuery,
  useUpdateCustomTacticMutation,
  useDeleteCustomTacticMutation,
  useLazyExportDataQuery,
} = pillarsActionPlanApi;
